/**
 * A moment in text: written once as a UTC instant, read in the reader's zone.
 *
 * Text that is stored and read by several people — an alert's card, a note on
 * the board — has no single reader to write a time for. So it holds the
 * instant itself (`2026-10-18T03:00:00Z`), which every reader can resolve and
 * nobody can misread, and whatever shows it to a person writes it in that
 * person's clock: the board in the viewer's browser, a mail in its
 * recipient's zone. A reader with no renderer — a tool reading the raw text —
 * still holds the exact instant.
 *
 * Only a full instant with its `Z` is a moment. A bare date is a calendar day,
 * which has no zone to move between, and an instant without `Z` names no
 * clock to read it from. One inside code, or inside an address, is the text
 * itself and stays as written.
 */

/** A UTC instant to the minute, second or millisecond, standing alone. */
export const MOMENT =
  /(?<![\w:.\-/=])\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?Z(?![\w/])/

/** Every moment in `text`, each with where it starts. */
export function momentsOf(text: string) {
  return text.matchAll(new RegExp(MOMENT.source, 'g'))
}

/** An instant as text holds it: UTC, to the second. */
export function momentOf(at: Date): string {
  return at.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

function formatter(zone: string | undefined) {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: zone,
  })
}

/**
 * An instant in words, on `zone`'s clock — the runtime's own where none is
 * named, which in a browser is its reader's. A zone this runtime cannot read
 * is UTC rather than a failure: a moment shown on the wrong clock still names
 * that clock, and a page that throws shows nothing. An instant no calendar
 * holds (February 31st) is left as written rather than moved to another day.
 */
export function momentWords(iso: string, zone?: string): string {
  const at = new Date(iso)
  if (
    Number.isNaN(at.getTime()) ||
    at.toISOString().slice(0, 16) !== iso.slice(0, 16)
  ) {
    return iso
  }
  try {
    return formatter(zone).format(at)
  } catch {
    return formatter('UTC').format(at)
  }
}

/** Code in markdown: a fenced block, or an inline span. */
const CODE = /(```[\s\S]*?```|`[^`\n]*`)/

/**
 * Every moment in markdown `text`, written on `zone`'s clock — the way the
 * board draws the same text, so a mail and its card say one thing. Code is
 * passed over: there an instant is the text itself.
 */
export function momentsIn(text: string, zone?: string): string {
  return text
    .split(CODE)
    .map((part, i) =>
      i % 2 === 1
        ? part
        : part.replace(new RegExp(MOMENT.source, 'g'), (iso) =>
            momentWords(iso, zone),
          ),
    )
    .join('')
}
